import { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, Phone, ChevronDown, ChevronRight, Tag } from "lucide-react";
import logo from "@/assets/logo.png";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { mainNavigation } from "@/lib/siteStructure";

const productCategories = [
  {
    title: "Selective Access",
    items: [
      { label: "Selective Racking", href: "/products/selective", sub: "Teardrop · Structural · Tubular" },
      { label: "VNA Racking", href: "/products/vna" },
      { label: "Cantilever Racking", href: "/products/cantilever" },
    ],
  },
  {
    title: "High-Density",
    items: [
      { label: "Double-Deep Racking", href: "/products/double-deep" },
      { label: "Drive-In Systems", href: "/products/drive-in" },
      { label: "Drive-Through Systems", href: "/products/drive-through" },
      { label: "Push-Back Racking", href: "/products/push-back" },
      { label: "Pallet Flow Racking", href: "/products/pallet-flow" },
      { label: "Mobile Racking", href: "/products/mobile" },
    ],
  },
  {
    title: "Automated & Portable",
    items: [
      { label: "Shuttle Racking", href: "/products/shuttle" },
      { label: "AS/RS Systems", href: "/products/asrs" },
      { label: "Clad Rack Buildings", href: "/products/clad-rack" },
      { label: "Stack Racks", href: "/products/stack-racks" },
      { label: "Post Pallet Racks", href: "/products/post-pallets" },
    ],
  },
];

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);

  const nonProductNav = mainNavigation.filter((item) => item.label !== "Products");

  return (
    <>
    <a href="#main-content" className="skip-to-content">
      Skip to main content
    </a>
    <header className="sticky top-0 z-50 w-full border-b border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80">
      {/* Top bar */}
      <div className="bg-primary text-primary-foreground">
        <div className="container flex h-10 items-center justify-between text-sm">
          <div className="flex items-center gap-6">
            <span className="hidden sm:inline">Nationwide Warehouse Racking Broker</span>
            <span className="flex items-center gap-1.5">
              <Phone className="h-3.5 w-3.5" />
              <a href="tel:1-888-315-7225" className="hover:text-accent transition-colors">
                1-888-315-RACK
              </a>
            </span>
          </div>
          <div className="hidden md:flex items-center gap-4 text-primary-foreground/80">
            <span>Quick Ship Available</span>
            <span className="text-accent font-semibold">•</span>
            <span>Quality Guaranteed</span>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <div className="container">
        <div className="flex h-16 items-center justify-between">
          <Link to="/" className="flex items-center">
            <img src={logo} alt="PalletRackingUSA" className="h-12 w-auto" width="72" height="48" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1" aria-label="Main navigation">
            {/* Products Mega Menu */}
            <div
              className="relative"
              onMouseEnter={() => setActiveDropdown("Products")}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <Link
                to="/products"
                className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-foreground hover:text-accent transition-colors rounded-md hover:bg-muted"
              >
                Products
                <ChevronDown className="h-4 w-4" />
              </Link>

              <AnimatePresence>
                {activeDropdown === "Products" && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.15 }}
                    className="absolute top-full left-1/2 -translate-x-1/2 pt-2 w-[680px] z-50"
                  >
                    <div className="rounded-xl bg-card border border-border shadow-elevated p-6">
                    <div className="grid grid-cols-3 gap-6">
                      {productCategories.map((cat) => (
                        <div key={cat.title}>
                          <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                            {cat.title}
                          </h4>
                          <ul className="space-y-1">
                            {cat.items.map((item) => (
                              <li key={item.href}>
                                <Link
                                  to={item.href}
                                  className="block px-3 py-1.5 text-sm text-foreground hover:bg-muted hover:text-accent rounded-md transition-colors"
                                >
                                  {item.label}
                                  {"sub" in item && item.sub && (
                                    <span className="block text-xs text-muted-foreground font-normal mt-0.5">{item.sub}</span>
                                  )}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                    <div className="mt-5 pt-4 border-t border-border">
                      <Link
                        to="/products"
                        className="inline-flex items-center gap-1.5 text-sm font-medium text-accent hover:underline"
                      >
                        View All Products
                        <ChevronRight className="h-4 w-4" />
                      </Link>
                    </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Other nav items */}
            {nonProductNav.map((item) => (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => item.children && setActiveDropdown(item.label)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <Link
                  to={item.href}
                  className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-foreground hover:text-accent transition-colors rounded-md hover:bg-muted"
                >
                  {item.label}
                  {item.children && <ChevronDown className="h-4 w-4" />}
                </Link>

                <AnimatePresence>
                  {item.children && activeDropdown === item.label && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      transition={{ duration: 0.15 }}
                      className="absolute top-full left-0 pt-2 w-56 z-50"
                    >
                      <div className="rounded-lg bg-card border border-border shadow-elevated p-2">
                      {item.children.map((child) => (
                        <Link
                          key={child.label}
                          to={child.href}
                          className="block px-4 py-2.5 text-sm text-foreground hover:bg-muted hover:text-accent rounded-md transition-colors"
                        >
                          {child.label}
                        </Link>
                      ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </nav>

          {/* CTA Buttons */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="hidden sm:inline-flex border-accent/40 text-accent hover:bg-accent hover:text-accent-foreground font-semibold"
              onClick={() => window.dispatchEvent(new Event("open-deals-popup"))}
            >
              <Tag className="h-3.5 w-3.5 mr-1.5" />
              Deals
            </Button>
            <Button variant="accent" size="lg" className="hidden sm:inline-flex" asChild>
              <Link to="/quote" data-track-cta="header_get_quote">Get a Quote</Link>
            </Button>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-foreground hover:text-accent"
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" aria-hidden="true" /> : <Menu className="h-6 w-6" aria-hidden="true" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden border-t border-border bg-card"
          >
            <nav className="container py-4 space-y-2 max-h-[70vh] overflow-y-auto" aria-label="Mobile navigation">
              {/* Products with expandable categories */}
              <div>
                <button
                  onClick={() => setMobileExpanded(mobileExpanded === "Products" ? null : "Products")}
                  className="flex items-center justify-between w-full px-4 py-3 text-foreground font-medium hover:text-accent hover:bg-muted rounded-md"
                >
                  <span>Products</span>
                  <ChevronDown className={`h-4 w-4 transition-transform ${mobileExpanded === "Products" ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence>
                  {mobileExpanded === "Products" && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="ml-4 space-y-3 mt-1 overflow-hidden"
                    >
                      <Link
                        to="/products"
                        className="block px-4 py-2 text-sm font-medium text-accent"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        View All Products →
                      </Link>
                      {productCategories.map((cat) => (
                        <div key={cat.title}>
                          <div className="px-4 py-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            {cat.title}
                          </div>
                          {cat.items.map((item) => (
                            <Link
                              key={item.href}
                              to={item.href}
                              className="block px-4 py-2 text-sm text-muted-foreground hover:text-accent"
                              onClick={() => setMobileMenuOpen(false)}
                            >
                              {item.label}
                            </Link>
                          ))}
                        </div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Other nav items */}
              {nonProductNav.map((item) => (
                <div key={item.label}>
                  {item.children ? (
                    <>
                      <button
                        onClick={() => setMobileExpanded(mobileExpanded === item.label ? null : item.label)}
                        className="flex items-center justify-between w-full px-4 py-3 text-foreground font-medium hover:text-accent hover:bg-muted rounded-md"
                      >
                        <span>{item.label}</span>
                        <ChevronDown className={`h-4 w-4 transition-transform ${mobileExpanded === item.label ? "rotate-180" : ""}`} />
                      </button>
                      <AnimatePresence>
                        {mobileExpanded === item.label && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            className="ml-4 mt-1 space-y-1 overflow-hidden"
                          >
                            {item.children.map((child) => (
                              <Link
                                key={child.label}
                                to={child.href}
                                className="block px-4 py-2 text-sm text-muted-foreground hover:text-accent"
                                onClick={() => setMobileMenuOpen(false)}
                              >
                                {child.label}
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </>
                  ) : (
                    <Link
                      to={item.href}
                      className="block px-4 py-3 text-foreground font-medium hover:text-accent hover:bg-muted rounded-md"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {item.label}
                    </Link>
                  )}
                </div>
              ))}

              <div className="pt-4 px-4 space-y-2">
                <Button
                  variant="outline"
                  size="lg"
                  className="w-full border-accent/40 text-accent hover:bg-accent hover:text-accent-foreground font-semibold"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    window.dispatchEvent(new Event("open-deals-popup"));
                  }}
                >
                  <Tag className="h-4 w-4 mr-2" />
                  Deals
                </Button>
                <Button variant="accent" size="lg" className="w-full" asChild>
                  <Link to="/quote" onClick={() => setMobileMenuOpen(false)}>
                    Get a Quote
                  </Link>
                </Button>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
    </>
  );
}
